import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const SurveyAnalysis = ({ analysisData }) => {
  const {
    job_satisfaction,
    role_clarity,
    support_and_supervision,
    belonging_and_recognition,
    advocacy,
    upstart_experience,
    summary,
  } = analysisData;

  const chartData = {
    labels: [
      "Job Satisfaction",
      "Role Clarity",
      "Support & Supervision",
      "Belonging & Recognition",
      "Advocacy",
      "Upstart Experience",
    ],
    datasets: [
      {
        label: "Survey Score",
        data: [
          job_satisfaction,
          role_clarity,
          support_and_supervision,
          belonging_and_recognition,
          advocacy,
          upstart_experience,
        ],
        backgroundColor: "rgba(59, 130, 246, 0.8)",
        borderColor: "rgba(59, 130, 246, 1)",
        borderWidth: 2,
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        min: 0,
        ticks: {
          stepSize: 20,
          color: "#6B7280",
          font: {
            size: 12,
            weight: "500",
          },
        },
        grid: {
          color: "rgba(107, 114, 128, 0.1)",
        },
      },
      x: {
        ticks: {
          color: "#374151",
          font: {
            size: 10,
            weight: "500",
          },
          maxRotation: 0,
          minRotation: 0,
          callback: function (value, index, ticks) {
            const label = this.getLabelForValue(value);
            // Split long labels into multiple lines
            if (label.length > 12) {
              const words = label.split(" ");
              if (words.length > 1) {
                const midPoint = Math.ceil(words.length / 2);
                const firstLine = words.slice(0, midPoint).join(" ");
                const secondLine = words.slice(midPoint).join(" ");
                return [firstLine, secondLine];
              }
            }
            return label;
          },
        },
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#fff",
        bodyColor: "#fff",
        borderColor: "rgba(251, 191, 36, 1)",
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: function (context) {
            return `${context.label}: ${context.parsed.y}%`;
          },
        },
      },
    },
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-600 bg-green-100";
    if (score >= 60) return "text-blue-600 bg-blue-100";
    if (score >= 40) return "text-yellow-600 bg-yellow-100";
    return "text-red-600 bg-red-100";
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return "Excellent";
    if (score >= 60) return "Good";
    if (score >= 40) return "Fair";
    return "Needs Improvement";
  };

  const metrics = [
    { name: "Job Satisfaction", value: job_satisfaction },
    { name: "Role Clarity", value: role_clarity },
    { name: "Support & Supervision", value: support_and_supervision },
    { name: "Belonging & Recognition", value: belonging_and_recognition },
    { name: "Advocacy", value: advocacy },
    { name: "Upstart Experience", value: upstart_experience },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-4">
          <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2">
            Thank you for completing your survey!
          </h2>

          <p className="text-xs text-gray-600 max-w-lg mx-auto">
            Here are your detailed results and analysis
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3">
          {/* Bar Chart */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-base font-bold text-gray-900 mb-3 text-center">
              Survey Results Overview
            </h2>
            <div className="h-80 w-full pb-4">
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>

          {/* Metrics Breakdown */}
          <div className="bg-white rounded-lg shadow-sm p-3">
            <h2 className="text-base font-bold text-gray-900 mb-2 text-center">
              Detailed Results
            </h2>
            <div className="space-y-2">
              {metrics.map((metric) => (
                <div
                  key={metric.name}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded-md"
                >
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900 text-xs">
                      {metric.name}
                    </h3>
                    <div className="flex items-center mt-1">
                      <div className="flex-1 bg-gray-200 rounded-full h-1.5 mr-2">
                        <div
                          className={`h-1.5 rounded-full transition-all duration-500 ${
                            metric.value >= 80
                              ? "bg-green-500"
                              : metric.value >= 60
                              ? "bg-blue-500"
                              : metric.value >= 40
                              ? "bg-yellow-500"
                              : "bg-red-500"
                          }`}
                          style={{ width: `${metric.value}%` }}
                        ></div>
                      </div>
                      <span
                        className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${getScoreColor(
                          metric.value
                        )}`}
                      >
                        {metric.value}%
                      </span>
                    </div>
                  </div>
                  <div className="ml-2 text-right">
                    <div
                      className={`text-sm font-bold ${
                        getScoreColor(metric.value).split(" ")[0]
                      }`}
                    >
                      {metric.value}
                    </div>
                    <div className="text-xs text-gray-500">
                      {getScoreLabel(metric.value)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Summary Section */}
        <div className="mt-3 bg-white rounded-lg shadow-sm p-3">
          <h2 className="text-base font-bold text-gray-900 mb-2 text-center">
            Your Analysis Summary
          </h2>
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-md p-2">
            <p className="text-gray-800 leading-relaxed text-xs">{summary}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyAnalysis;
